import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { API } from "../lib/api";

const SocketContext = createContext(null);

export const SocketProvider = ({ children }) => {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const listenersRef = useRef(new Set());

  const subscribe = useCallback((listener) => {
    listenersRef.current.add(listener);
    return () => listenersRef.current.delete(listener);
  }, []);

  useEffect(() => {
    if (!user) {
      setNotifications([]); setUnread(0); setConnected(false);
      return;
    }
    const token = localStorage.getItem("focusbee_token");
    if (!token) return;

    // Load existing notifications
    fetch(`${API}/notifications`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((data) => {
        setNotifications(data.items || []);
        setUnread(data.unread || 0);
      })
      .catch(() => {});

    // Open WebSocket
    const wsUrl = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws") + `/api/ws?token=${encodeURIComponent(token)}`;
    let ws;
    let pingInterval;
    try {
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;
    } catch (e) {
      return;
    }

    ws.onopen = () => {
      setConnected(true);
      // heartbeat to keep connection alive
      pingInterval = setInterval(() => {
        if (ws.readyState === 1) { try { ws.send("ping"); } catch { /* noop */ } }
      }, 25000);
    };
    ws.onclose = () => {
      setConnected(false);
      if (pingInterval) clearInterval(pingInterval);
    };
    ws.onerror = () => setConnected(false);
    ws.onmessage = (event) => {
      let data;
      try { data = JSON.parse(event.data); } catch { return; }
      if (data.type === "notification") {
        setNotifications((prev) => [data.data, ...prev].slice(0, 100));
        setUnread((u) => u + 1);
      }
      listenersRef.current.forEach((l) => { try { l(data); } catch { /* noop */ } });
    };

    return () => {
      if (pingInterval) clearInterval(pingInterval);
      try { ws.close(); } catch { /* noop */ }
    };
  }, [user]);

  const markAllRead = useCallback(async () => {
    const token = localStorage.getItem("focusbee_token");
    if (!token) return;
    try {
      await fetch(`${API}/notifications/read-all`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch { /* noop */ }
    setNotifications((n) => n.map((x) => ({ ...x, read: true })));
    setUnread(0);
  }, []);

  return (
    <SocketContext.Provider value={{ connected, notifications, unread, markAllRead, subscribe }}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => useContext(SocketContext);
